import {
  Col,
  Collapse,
  type CollapseProps,
  Form,
  type FormInstance,
  Row,
  Switch,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { flexLayout, type TFlexLayoutProps } from '@client/utils/layout.util';
import {
  CNsDiscount,
  CNsDuration,
  type ECurrency,
  type EDurationTypes,
} from '@common/types';

import styles from './rebate.module.less';
import { ChildrenComponent } from './sections/ChildrenComponent';
import { DiscountedComponent } from './sections/DiscountedComponent';
import { OriginalPrice } from './sections/OriginalPrice';
import { PeriodicPayment } from './sections/PeriodicPayment';

type TDiscountProps = {
  formRef: FormInstance<any>;
  disabled: boolean;
  children?: React.ReactNode;
  loading: boolean;
  discountHeader?: string;
  collapsed?: boolean;
  collapsible?: boolean;
  durationTypes?: (keyof typeof EDurationTypes)[];
  canBeTrialed?: boolean;
  canBeDiscounted?: boolean;
  canBePeriodic?: boolean;
  priceMin?: number;
  namespace?: string;
  prefix?: string[];
  currencies?: (keyof typeof ECurrency)[];
  readOnlyFields?: string[];
  header?: string;
  childrenColProps?: TFlexLayoutProps;
  setIsTrialed?: (checked: boolean) => void;
  onChangeCurrency?: (currency: keyof typeof ECurrency) => void;
  renderScheduler?: (readOnlyFields?: string[]) => React.ReactNode;
};

export const Discount: React.FC<TDiscountProps> = (props) => {
  const { t } = useTranslation();

  const {
    formRef,
    disabled,
    children,
    loading,
    discountHeader,
    collapsed = false,

    durationTypes = [],

    canBeTrialed = false,
    canBeDiscounted = false,
    canBePeriodic = false,

    priceMin = 1,
    namespace = CNsDiscount,
    prefix = [],
    currencies = [],
    readOnlyFields = [],

    header = t('price.priceInfo', { type: '' }),
    childrenColProps = flexLayout.fullColumn,

    setIsTrialed,
    renderScheduler,
  } = props;

  const isTrialedChecked: boolean = formRef.getFieldValue('isTrialed');

  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  const handleTrialToggling = useCallback(
    (checked: boolean) => {
      setIsTrialed?.(checked);
    },
    [setIsTrialed],
  );

  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  useEffect(() => {
    handleTrialToggling(isTrialedChecked);
  }, [isTrialedChecked, handleTrialToggling]);

  const sharedProps = {
    formRef,
    disabled,
    loading,
    namespace,
    prefix,
    readOnlyFields,
  };

  const originalPriceProps = {
    currencies,
    priceMin,
    ...sharedProps,
  };

  const periodicPaymentProps = {
    canBePeriodic,
    durationTypes,
    ...sharedProps,
  };

  const discountedProps = {
    collapsible: false,
    discountHeader,
    canBeDiscounted,
    renderScheduler,
    ...sharedProps,
  };

  const discountItems: CollapseProps['items'] = [
    {
      key: namespace,
      label: header,
      forceRender: true,
      extra: canBeTrialed ? (
        <div
          onClick={(e) => e.stopPropagation()}
          className={styles.extraSwitch}
        >
          <Form.Item
            layout="horizontal"
            name={'isTrialed'}
            label={t('price.trialed')}
          >
            <Switch
              disabled={disabled}
              loading={loading}
              defaultChecked={isTrialedChecked}
              onChange={handleTrialToggling}
            />
          </Form.Item>
        </div>
      ) : null,
      children: (
        <>
          <Row gutter={[24, 0]}>
            <Col {...flexLayout.halfColumn}>
              <OriginalPrice {...originalPriceProps} />
            </Col>
            <Col {...flexLayout.halfColumn}>
              <PeriodicPayment
                {...periodicPaymentProps}
                namespace={CNsDuration}
              />
            </Col>
          </Row>
          <ChildrenComponent
            children={children}
            childrenColProps={childrenColProps as TFlexLayoutProps}
          />
          <DiscountedComponent {...discountedProps} />
        </>
      ),
    },
  ];

  const defaultActiveKey = isCollapsed ? null : [namespace];

  return (
    <Collapse
      items={discountItems}
      className={styles.discount}
      collapsible={'disabled'}
      defaultActiveKey={defaultActiveKey as string[]}
    />
  );
};
